import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

/* Per-component paths, not the design-system barrel: the barrel would pull
   every design-system component (SideSheet, Avatar...) into this chunk. */
import { Button } from "../../design-system/components/Button";
import { Card } from "../../design-system/components/Card";
import { Modal } from "../../design-system/components/Modal";
import {
  CHOOSE_OUTCOME,
  chooseContext,
  selectActiveSquadId,
  selectSwitchableContexts,
} from "../../store/user/slice";

/**
 * EXPERIENCE SWITCHER: ONE CARD PER EXPERIENCE.
 *
 * Shown to a learner in two or more delivery contexts: in place of the
 * unavailable screen when none is chosen (ExperienceContextGate), and in a
 * Modal from the Sidebar switch. Every label comes from discovery and any of
 * them may be null, so each line has a fallback and none is ever invented.
 */

/* A4: a `Y-m-d` season date is a calendar day, not an instant. `new
   Date("2026-01-05")` is UTC midnight, which is Jan 4 in every US time zone,
   so it is built from its parts in local time instead. */
export const parseLocalDate = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd ?? "");
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

const monthDay = (date) =>
  date
    .toLocaleDateString("en-US", { month: "short", day: "numeric" })
    .toLowerCase();

/** "jan 5 to mar 9, 2026", "starts jan 5, 2026", or null. */
export const formatSeason = (start, end) => {
  const from = parseLocalDate(start);
  const to = parseLocalDate(end);
  if (from && to) {
    return from.getFullYear() === to.getFullYear()
      ? `${monthDay(from)} to ${monthDay(to)}, ${to.getFullYear()}`
      : `${monthDay(from)}, ${from.getFullYear()} to ${monthDay(to)}, ${to.getFullYear()}`;
  }
  if (from) return `starts ${monthDay(from)}, ${from.getFullYear()}`;
  if (to) return `ends ${monthDay(to)}, ${to.getFullYear()}`;
  return null;
};

/**
 * What a card (and the Sidebar switch) says about one context. The title
 * falls back through the program, cohort and squad names; the partner line
 * falls back to the cohort title or the squad name (A2: a cohort linked only
 * through `cohort_partners` has no partner here), and is dropped when it
 * would only repeat the title.
 */
export const describeContext = (context, ordinal = null) => {
  const labels = context?.labels ?? {};
  const title =
    labels.challenge_title ??
    labels.program_name ??
    labels.cohort_title ??
    labels.squad_name ??
    /* Every label null: a stable ordinal from discovery's own order (cohort,
       then squad), so two cards never read the same. */
    (ordinal ? `experience ${ordinal}` : "your experience");
  const partnerLine =
    labels.partner_name ?? labels.cohort_title ?? labels.squad_name ?? null;
  return {
    title,
    partner: partnerLine === title ? null : partnerLine,
    squad: labels.squad_name,
    season: formatSeason(labels.season_start, labels.season_end),
  };
};

const BLOCKED_NOTE =
  "your browser blocked the switch. allow site data for this page, then try again.";

/** The cards. `firstButtonRef` receives the first enabled button, for focus. */
export const ExperienceChooser = ({ firstButtonRef = null }) => {
  const dispatch = useDispatch();
  const contexts = useSelector(selectSwitchableContexts);
  const activeSquadId = useSelector(selectActiveSquadId);
  const [blocked, setBlocked] = useState(false);
  const [pendingSquadId, setPendingSquadId] = useState(null);

  // The list belongs to one discovery; a new list starts with no note.
  useEffect(() => {
    setBlocked(false);
    setPendingSquadId(null);
  }, [contexts]);

  const choose = (squadId) => {
    const outcome = dispatch(chooseContext(squadId));
    setBlocked(outcome === CHOOSE_OUTCOME.BLOCKED);
    setPendingSquadId(outcome === CHOOSE_OUTCOME.RELOADING ? squadId : null);
  };

  let firstEnabledSeen = false;
  return (
    <div className="flex flex-col gap-3">
      {blocked ? (
        <p role="alert" className="text-base text-text-lv3">
          {BLOCKED_NOTE}
        </p>
      ) : null}
      <ul
        className="m-0 flex list-none flex-col gap-3 p-0"
        aria-label="your experiences"
      >
        {contexts.map((context, index) => {
          const { title, partner, squad, season } = describeContext(
            context,
            index + 1,
          );
          const isActive = context.squad_id === activeSquadId;
          const isFirstEnabled = !isActive && !firstEnabledSeen;
          if (isFirstEnabled) firstEnabledSeen = true;
          return (
            <Card
              key={context.squad_id}
              as="li"
              variant={isActive ? "outlined" : "elevated"}
              padding="lg"
              className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${
                isActive ? "!border-pear/60" : ""
              }`}
              data-testid="experience-card"
            >
              <div className="flex min-w-0 flex-col gap-1">
                {partner ? (
                  <p className="text-xs uppercase tracking-widest text-text-lv4 [overflow-wrap:anywhere]">
                    {partner}
                  </p>
                ) : null}
                <h3 className="text-md font-medium text-text-lv0 [overflow-wrap:anywhere]">
                  {title}
                </h3>
                <p className="flex flex-wrap gap-x-3 gap-y-1 text-base text-text-lv3">
                  {squad ? (
                    <span className="[overflow-wrap:anywhere]">
                      {/^squad\b/i.test(squad) ? squad : `squad ${squad}`}
                    </span>
                  ) : null}
                  {season ? <span>{season}</span> : null}
                </p>
              </div>
              {isActive ? (
                <p className="flex shrink-0 items-center gap-2 text-base text-pear">
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full bg-pear"
                  />
                  you&apos;re here
                </p>
              ) : (
                <Button
                  ref={isFirstEnabled ? firstButtonRef : undefined}
                  variant="tonal"
                  level="secondary"
                  size="md"
                  className="shrink-0 self-start sm:self-auto"
                  loading={pendingSquadId === context.squad_id}
                  disabled={pendingSquadId !== null}
                  aria-label={`open ${title}`}
                  onClick={() => choose(context.squad_id)}
                >
                  open
                </Button>
              )}
            </Card>
          );
        })}
      </ul>
    </div>
  );
};

/** The chooser in place of the unavailable screen (nothing chosen yet). */
export const ExperienceChooserScreen = () => (
  <section
    className="flex w-full justify-center p-4 sm:p-6"
    aria-labelledby="experience-chooser-title"
    data-testid="experience-chooser"
  >
    <div className="flex w-full max-w-2xl flex-col gap-5 py-6 sm:py-12">
      <div className="flex flex-col gap-2">
        <h2
          id="experience-chooser-title"
          className="text-lg font-medium text-text-lv0"
        >
          pick your experience
        </h2>
        <p className="text-md text-text-lv3">
          you&apos;re in more than one. you can switch any time from the menu.
        </p>
      </div>
      <ExperienceChooser />
    </div>
  </section>
);

/** The same chooser in the design-system Modal (Sidebar and gate states). */
const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Focus stays inside the dialog while it is open (Tab and Shift+Tab wrap), and
 * returns to whatever opened it when it closes, by Escape, the close button,
 * or the backdrop. Lives here, not in the shared design-system Modal, whose
 * other consumers are out of scope.
 */
const useContainedFocus = (isOpen, contentRef, initialRef) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const dialogOf = () => contentRef.current?.closest('[role="dialog"]');
    const focusables = () =>
      Array.from(dialogOf()?.querySelectorAll(FOCUSABLE) ?? []).filter(
        (element) => !element.disabled,
      );
    (initialRef.current ?? focusables()[0])?.focus();
    const onKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const dialog = dialogOf();
      const items = focusables();
      if (!dialog || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !dialog.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, [isOpen, contentRef, initialRef]);
};

export const ExperienceSwitcherModal = ({ isOpen, onClose }) => {
  const firstButtonRef = useRef(null);
  const contentRef = useRef(null);
  useContainedFocus(isOpen, contentRef, firstButtonRef);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      aria-labelledby="experience-switcher-title"
      data-testid="experience-switcher-modal"
    >
      <div ref={contentRef}>
        <Modal.Header>
          <Modal.Title id="experience-switcher-title">
            switch experience
          </Modal.Title>
          <Modal.Description>pick where you want to work.</Modal.Description>
        </Modal.Header>
        <Modal.Body className="max-h-[70vh] overflow-y-auto pb-6">
          <ExperienceChooser firstButtonRef={firstButtonRef} />
        </Modal.Body>
      </div>
    </Modal>
  );
};
